import { yupResolver } from '@hookform/resolvers/yup';

import { LoadingButton } from '@mui/lab';
import {
  Grid,
  Stack,
  Dialog,
  Button,
  Divider,
  MenuItem,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import TextField from '@mui/material/TextField';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import useProductOptions from 'src/hooks/use-product-options';
import { useBoolean } from 'src/hooks/use-boolean';
import { fNumber } from 'src/utils/format-number';
import useProductFeeOptions from 'src/hooks/use-product-fee-options';
import Iconify from 'src/components/iconify';

import axios, { endpoints } from 'src/utils/axios';
import { useEffect } from 'react';
import useCurrentDateTime from 'src/hooks/use-current-date-time';

const defaultValues = {
  productId: '',
  amount: 0,
  productRedeemFees: [{ id: '', amount: 0 }],
  unitType: 'BAHT',
};

const Schema = Yup.object().shape({
  productId: Yup.string().required('กรุณาเลือกสินค้า'),
  amount: Yup.number()
    .typeError('น้ำหนักต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุน้ำหนัก')
    .moreThan(-1, 'น้ำหนักขั้นต่ำ 0'),
  productRedeemFees: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required('จำเป็นต้องระบุค่าธรรมเนียม'),
      amount: Yup.number()
        .typeError('จำนวนต้องเป็นตัวเลข')
        .required('จำเป็นต้องระบุจำนวน')
        .moreThan(0, 'จำนวนขั้นต่ำ 1'),
    })
  ),
  unitType: Yup.string().required('กรุณาเลือกหน่วยของทองคำ'),
});

type Props = {
  open: boolean;
  onClose: VoidFunction;
  handleReload: VoidFunction;
};

export default function StockExportCreateDialog({ open, onClose, handleReload }: Props) {
  const currentDateTime = useCurrentDateTime();

  const amountError = useBoolean();

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'productRedeemFees',
  });

  const handleAdd = () => {
    append({
      id: '',
      amount: 0,
    });
  };

  const handleRemove = (index: number) => {
    remove(index);
  };

  const handleProductRedeemFeeAmountInput = (index: number, value: string) => {
    let processedValue = value;
    if (processedValue && processedValue.startsWith('.')) {
      processedValue = `0${processedValue}`;
    }
    setValue(`productRedeemFees.${index}.amount`, processedValue as unknown as number, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const { productOptions } = useProductOptions(true);
  // const { branchOptions } = useBranchOptions();
  const selectedProduct = productOptions.find((item) => item.value === watch().productId);
  const isBar96 =
    selectedProduct &&
    selectedProduct?.label.includes('แท่ง') &&
    selectedProduct?.label.includes('96');

  const { productFeeOptions } = useProductFeeOptions(
    watch().productId,
    watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'
  );

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (totalWeight !== Number(values.amount)) {
        throw new Error('ขนาดทองไม่ตรงกับจำนวนทองที่ต้องการแลกเปลี่ยน');
      }

      const body = {
        productId: values.productId,
        amount: Number(values.amount),
        productRedeemFees: values.productRedeemFees,
        currentDateTime,
        unitType: isBar96 ? values.unitType : 'GRAM',
      };

      await axios.post(endpoints.transactions.redeem.stock, body);

      handleReload();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });

      beforeClose();
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const feeWithInfo = watch().productRedeemFees?.map((fee) => {
    const redeemFee = productFeeOptions.find((option) => option.value === fee.id)?.info;
    const weight =
      watch().unitType === 'BAHT' ? redeemFee?.weightBaht || 0 : redeemFee?.weightGram || 0;
    return {
      ...fee,
      weight,
    };
  });

  const totalWeight = feeWithInfo?.reduce((sum, item) => sum + item.amount * item.weight, 0) || 0;

  useEffect(() => {
    if (!isBar96) {
      setValue('unitType', 'GRAM');
    }
  }, [isBar96]);

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>สร้างรายการส่งออกสินค้า</DialogTitle>

        <DialogContent>
          <Stack spacing={3} py={1}>
            <RHFSelect name="productId" label="สินค้า" placeholder="เลือกสินค้า">
              {productOptions
                .filter((item) => !item.label.includes('รูปพรรณ'))
                .map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
            </RHFSelect>

            <Stack
              spacing={2}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              {!!isBar96 && (
                <RHFSelect name="unitType" label="หน่วยทองคำ">
                  <MenuItem value="BAHT">บาท</MenuItem>
                  <MenuItem value="GRAM">กรัม</MenuItem>
                </RHFSelect>
              )}
              <RHFTextField
                name="amount"
                type="number"
                label="น้ำหนัก"
                placeholder="0"
                InputLabelProps={{ shrink: true }}
                error={amountError.value}
                helperText={amountError.value ? 'น้ำหนักเกินกว่าที่สามารถทำรายการได้' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography fontWeight="bold" color="success.main">
                        {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: { xs: 1, sm: '1 1 auto' } }}
              />
            </Stack>

            <Stack spacing={3}>
              <Stack spacing={3}>
                <Typography variant="caption" color="action.disabled">
                  ขนาดทอง
                </Typography>
                <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />} spacing={2}>
                  {fields.map((item, index) => (
                    <Stack key={item.id} spacing={1.5} alignItems="flex-end">
                      <Grid container spacing={2} sx={{ width: 1 }}>
                        <Grid item xs={12} sm={8}>
                          <RHFSelect
                            size="small"
                            name={`productRedeemFees[${index}].id`}
                            label="ทอง"
                            InputLabelProps={{ shrink: true }}
                            placeholder="ทอง"
                            disabled={!watch().productId}
                            fullWidth
                          >
                            {productFeeOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </RHFSelect>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                          >
                            <Controller
                              name={`productRedeemFees.${index}.amount`}
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  {...field}
                                  size="small"
                                  type="number"
                                  label="จำนวน"
                                  inputMode="numeric"
                                  placeholder="0"
                                  InputLabelProps={{ shrink: true }}
                                  disabled={!watch().productId}
                                  error={!!error}
                                  helperText={error?.message}
                                  fullWidth
                                  sx={{ flex: { xs: 1, sm: '1 1 auto' } }}
                                  value={field.value === 0 ? '' : field.value}
                                  onChange={(event) => {
                                    const { value } = event.target;
                                    handleProductRedeemFeeAmountInput(index, value);
                                  }}
                                />
                              )}
                            />
                            {fields.length > 1 && (
                              <Button
                                size="small"
                                color="error"
                                startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                                onClick={() => handleRemove(index)}
                                sx={{
                                  mt: { xs: 0, sm: 0.5 },
                                  width: { xs: '100%', sm: 'auto' },
                                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                                }}
                              >
                                ลบ
                              </Button>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  ))}
                </Stack>

                <Stack spacing={3} direction="row" justifyContent="space-between">
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<Iconify icon="mingcute:add-line" />}
                    onClick={handleAdd}
                    sx={{ flexShrink: 0 }}
                    disabled={!watch().productId}
                  >
                    เพิ่ม
                  </Button>

                  <Stack alignItems="flex-end" sx={{ width: 1 }}>
                    <Typography
                      variant={totalWeight !== Number(watch().amount) ? 'subtitle1' : 'subtitle2'}
                      color={totalWeight !== Number(watch().amount) ? 'error' : 'success.main'}
                    >
                      น้ำหนักทองรวม {fNumber(totalWeight)}{' '}
                      {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                    </Typography>
                    <Typography variant="subtitle2">
                      น้ำหนักทองที่ถอน {fNumber(Number(watch().amount))}{' '}
                      {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                    </Typography>
                  </Stack>
                </Stack>
                <Divider flexItem sx={{ borderStyle: 'dashed' }} />
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            // disabled={isCreateAndApprove && !filters.name?.value && !user?.uid}
            disabled={amountError.value}
            loading={isSubmitting}
          >
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
